import React from "react";
import { arrayOf, bool, node, shape, string } from "prop-types";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import { Box } from "@mui/material";
import { Link } from "react-router";
import { cardHover } from "../component-styles";

const ToolCard = ({ tool, accessible = false }) => {
  const { href, to, name, description, details, image } = tool;
  return (
    <>
      <Card
        variant="outlined"
        sx={(theme) => ({
          borderColor: theme.palette.secondary.light,
          position: "relative",
          display: "flex",
          ...cardHover(theme.palette),
        })}
      >
        {!accessible ? <AccessFlag toolName={name} /> : null}
        <CardActionArea
          disabled={!accessible}
          sx={!accessible ? { pointerEvents: "none", opacity: ".4" } : undefined}
          component={to ? Link : "button"}
          href={accessible ? href : null}
          to={accessible ? to : null}
        >
          <CardContent
            sx={{
              display: "flex",
              alignItems: "stretch",
              flexWrap: "wrap",
              justifyContent: "center",
              height: "100%",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            <CardMedia
              image={image}
              title={name}
              sx={(theme) => ({
                minHeight: 120,
                minWidth: 200,
                flexBasis: 150,
                backgroundSize: "70%",
                backgroundPosition: "center center",
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(3),
                "@media (min-height: 900px)": {
                  minHeight: 120,
                  minWidth: 200,
                  backgroundSize: "70%",
                  marginTop: theme.spacing(1),
                },
              })}
            />
            <div style={{ flexBasis: "60%", flexGrow: "1" }}>
              <Typography
                gutterBottom
                variant="h5"
                component="h2"
                sx={(theme) => ({
                  color: theme.components.MuiButton.styleOverrides.text.color,
                  paddingBottom: "5px",
                  borderBottom: `3px solid ${theme.palette.primary.light}`,
                  marginBottom: theme.spacing(2),
                  paddingLeft: "15px",
                  paddingRight: "15px",
                  fontSize: 24,
                  "& span": {
                    display: "block",
                  },
                })}
              >
                <span>{name}</span>
              </Typography>
              <Typography
                variant="body2"
                component="p"
                sx={{
                  fontSize: 18,
                  fontWeight: 500,
                  letterSpacing: "0.15px",
                  lineHeight: "20px",
                  paddingLeft: "15px",
                  paddingRight: "15%",
                  color: "black",
                }}
              >
                {description}
              </Typography>
              <ToolDetails detailItems={details} />
            </div>
          </CardContent>
        </CardActionArea>
      </Card>
    </>
  );
};

ToolCard.propTypes = {
  tool: shape({
    href: string,
    to: string,
    name: string.isRequired,
    description: string,
    details: arrayOf(node),
    image: string,
  }).isRequired,
  accessible: bool,
};

const ToolDetails = ({ detailItems }) => {
  return (
    <Box
      component="ul"
      sx={(theme) => ({
        paddingLeft: "35px",
        paddingRight: theme.spacing(2),
        "& li": {
          fontSize: 16,
          marginBottom: theme.spacing(0.5),
        },
      })}
    >
      {detailItems.map((detailItem, i) => (
        <li key={i}> {detailItem} </li>
      ))}
    </Box>
  );
};

ToolDetails.propTypes = {
  detailItems: arrayOf(node).isRequired,
};

const AccessFlag = ({ toolName }) => {
  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: "25%",
        width: "100%",
        zIndex: 10,
        textAlign: "center",
        background: "#eadeb3",
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
      })}
    >
      <Typography
        variant="subtitle1"
        component="p"
        sx={(theme) => ({
          textAlign: "center",
          lineHeight: 1,
          marginBottom: theme.spacing(0.5),
        })}
      >
        Your organization currently does not <br /> have access to this tool
      </Typography>
      <Button
        size="small"
        color="secondary"
        target="_blank"
        href={`mailto:analytics@icer.org?subject=Access Request: ${toolName}`}
      >
        Request Access
      </Button>
    </Box>
  );
};

AccessFlag.propTypes = {
  toolName: string.isRequired,
};

export default ToolCard;
